import { HdBodygraphPlanetaryActivation } from '../../bodygraph/hd-bodygraph.models';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { buildHdLocksKeysRelationshipLocks } from './hd-locks-keys';
import { HdLocksKeysPeriodId } from './hd-locks-keys-metadata';

/**
 * Eén sleutel- of slot-vlag naast een chartnaam: het poort.lijn-label plus de
 * keynote die bij die badge hoort. Slot = de rol van de lock (+ kwalificatie);
 * sleutel = de twee poort-keynotes van de key-poort plus de lijn-keynote(s).
 */
export interface HdLocksKeysRelationshipHoldingVm {
  readonly kind: 'key' | 'lock';
  readonly gateText: string;
  readonly keynote: string;
}

export interface HdLocksKeysRelationshipPartyVm {
  readonly name: string;
  readonly color: string;
  readonly holdings: readonly HdLocksKeysRelationshipHoldingVm[];
}

/**
 * Eén lock-regel: de opener (sleutelhouder) staat links, de slothouder rechts,
 * zodat de pijl altijd naar rechts (naar het slot) wijst. `both` = wederzijds.
 */
export interface HdLocksKeysRelationshipRowVm {
  readonly id: string;
  readonly direction: 'forward' | 'both';
  readonly left: HdLocksKeysRelationshipPartyVm;
  readonly right: HdLocksKeysRelationshipPartyVm;
}

export interface HdLocksKeysRelationshipPersonInput {
  readonly name: string;
  readonly color: string;
  readonly activations: readonly HdBodygraphPlanetaryActivation[];
}

/**
 * Bouwt de rijen van de slot-sleutelrelatie-sectie (gedeeld door het L&K-paneel
 * en het Geavanceerd-paneel): per relevante lock beide personen met hun
 * sleutel/slot-vlaggen, keynote-bij-de-badge en de openingsrichting. Dubbels
 * (wie zowel slot als sleutel draagt) krijgen beide vlaggen.
 */
export function buildHdLocksKeysRelationshipRows(
  gateInfo: HdGateInfoService,
  lang: string,
  period: HdLocksKeysPeriodId,
  person1: HdLocksKeysRelationshipPersonInput,
  person2: HdLocksKeysRelationshipPersonInput,
): readonly HdLocksKeysRelationshipRowVm[] {
  const locks = buildHdLocksKeysRelationshipLocks(
    period,
    person1.activations.map(activation => activation.gate),
    person2.activations.map(activation => activation.gate),
  );

  const linesOn = (activations: readonly HdBodygraphPlanetaryActivation[], gate: number): number[] =>
    [...new Set(
      activations
        .filter(activation => activation.gate === gate)
        .map(activation => activation.line)
        .filter((line): line is number => line != null),
    )].sort((a, b) => a - b);

  const gateText = (activations: readonly HdBodygraphPlanetaryActivation[], gate: number): string => {
    const lines = linesOn(activations, gate);
    return lines.length > 0 ? lines.map(line => `${gate}.${line}`).join(', ') : `${gate}`;
  };

  const lockKeynote = (lockGate: number): string =>
    [
      gateInfo.locksKeysTextIn(`locks.${lockGate}.role`, lang),
      gateInfo.locksKeysTextIn(`locks.${lockGate}.roleQualifier`, lang),
    ].filter(part => part !== '').join(' · ');

  const keyKeynote = (keyGate: number, activations: readonly HdBodygraphPlanetaryActivation[]): string => {
    const translations = gateInfo.gateInfoIn(keyGate, lang).translations;
    const lineKeynotes = linesOn(activations, keyGate)
      .map(line => translations.lineKeynotes[line - 1] ?? '');
    return [translations.keynote1, translations.keynote2, ...lineKeynotes]
      .filter(part => part !== '').join(' · ');
  };

  const holdingsFor = (
    person: HdLocksKeysRelationshipPersonInput,
    hasKey: boolean,
    hasLock: boolean,
    keyGate: number,
    lockGate: number,
  ): HdLocksKeysRelationshipHoldingVm[] => {
    const holdings: HdLocksKeysRelationshipHoldingVm[] = [];
    if (hasKey) {
      holdings.push({ kind: 'key', gateText: gateText(person.activations, keyGate), keynote: keyKeynote(keyGate, person.activations) });
    }
    if (hasLock) {
      holdings.push({ kind: 'lock', gateText: gateText(person.activations, lockGate), keynote: lockKeynote(lockGate) });
    }
    return holdings;
  };

  return locks.map<HdLocksKeysRelationshipRowVm>(lock => {
    const party1: HdLocksKeysRelationshipPartyVm = {
      name: person1.name,
      color: person1.color,
      holdings: holdingsFor(person1, lock.person1HasKey, lock.person1HasLock, lock.keyGate, lock.lockGate),
    };
    const party2: HdLocksKeysRelationshipPartyVm = {
      name: person2.name,
      color: person2.color,
      holdings: holdingsFor(person2, lock.person2HasKey, lock.person2HasLock, lock.keyGate, lock.lockGate),
    };
    const opensRight = lock.person1HasKey && lock.person2HasLock;
    const opensLeft = lock.person2HasKey && lock.person1HasLock;
    if (opensRight && opensLeft) {
      return { id: `rel-${lock.lockGate}`, direction: 'both', left: party1, right: party2 };
    }
    // Enkele richting: de sleutelhouder (opener) links, de slothouder rechts —
    // dan wijst de pijl altijd naar rechts, naar het slot dat geopend wordt.
    return opensRight
      ? { id: `rel-${lock.lockGate}`, direction: 'forward', left: party1, right: party2 }
      : { id: `rel-${lock.lockGate}`, direction: 'forward', left: party2, right: party1 };
  });
}
